import { Colors, Icon, TooltipWrapper } from "@zapier/design-system";
import { useCallback } from "react";
import { useAtom, useSetAtom } from "jotai";
import {
  cursorAtom,
  selectedNodeTypeAtom,
  CursorStates,
  selectedAssetIdAtom,
} from "@zapier/canvas-state";

import { PathConnectorIcon } from "../icons/PathConnectorIcon";
import { PointerIcon } from "../icons/PointerIcon";

import styles from "./DiagrammingToolbar.module.css";

type Props = { isDisabled: boolean };

export function CursorOptions({ isDisabled }: Props) {
  const [cursorMode, setCursorMode] = useAtom(cursorAtom);
  const [selectedNodeType, setSelectedNodeType] = useAtom(selectedNodeTypeAtom);
  const setSelectedAssetId = useSetAtom(selectedAssetIdAtom);

  const toolSelectionListItemClass = isDisabled
    ? styles.toolSelectionButtonWrapperDisabled
    : styles.toolSelectionButtonWrapper;

  const resetCursorMode = useCallback(() => {
    setCursorMode("select");
  }, [setCursorMode]);

  const clearSelectedNodeType = useCallback(() => {
    if (selectedNodeType !== null) {
      setSelectedNodeType(null); // Or else we will end up creating extra nodes at the same time
    }
  }, [selectedNodeType, setSelectedNodeType]);

  const maybeSetCursorMode = useCallback(
    (selectedCursorMode: CursorStates) => {
      setSelectedAssetId(null);

      if (selectedCursorMode === cursorMode) {
        resetCursorMode();
      } else {
        if (selectedCursorMode !== "select") {
          clearSelectedNodeType();
        }
        setCursorMode(selectedCursorMode);
      }
    },
    [
      cursorMode,
      setCursorMode,
      resetCursorMode,
      clearSelectedNodeType,
      setSelectedAssetId,
    ],
  );

  return (
    <ul className={styles.cursorList}>
      <li className={toolSelectionListItemClass}>
        <TooltipWrapper
          position="north"
          content={isDisabled ? "" : "Select tool"}
          tooltipId="select-tool"
        >
          {({ childProps }) => (
            <button
              aria-describedby={childProps["aria-describedby"]}
              disabled={isDisabled}
              onClick={() => maybeSetCursorMode("select")}
              onKeyDown={() => maybeSetCursorMode("select")}
              className={styles.toolSelectionButton}
            >
              <PointerIcon isSelected={cursorMode === "select"} />
            </button>
          )}
        </TooltipWrapper>
      </li>
      <li className={toolSelectionListItemClass}>
        <TooltipWrapper
          position="north"
          content={isDisabled ? "" : "Hand tool"}
          tooltipId="hand-tool"
        >
          {({ childProps }) => (
            <button
              aria-describedby={childProps["aria-describedby"]}
              disabled={isDisabled}
              onClick={() => maybeSetCursorMode("pan")}
              onKeyDown={() => maybeSetCursorMode("pan")}
              className={styles.toolSelectionButton}
            >
              <Icon
                name={cursorMode === "pan" ? "miscHandFill" : "miscHand"}
                size={24}
                color={
                  cursorMode === "pan" ? "UiPrimaryStrongest" : "currentColor"
                }
              />
            </button>
          )}
        </TooltipWrapper>
      </li>
      <li className={toolSelectionListItemClass}>
        <TooltipWrapper
          position="north"
          content={isDisabled ? "" : "Connector tool"}
          tooltipId="connector-tool"
        >
          {({ childProps }) => (
            <button
              aria-describedby={childProps["aria-describedby"]}
              disabled={isDisabled}
              onClick={() => maybeSetCursorMode("connect")}
              onKeyDown={() => maybeSetCursorMode("connect")}
              className={styles.toolSelectionButton}
            >
              <PathConnectorIcon
                color={
                  cursorMode === "connect"
                    ? Colors.UiPrimaryStrongest
                    : "currentColor"
                }
              />
            </button>
          )}
        </TooltipWrapper>
      </li>
    </ul>
  );
}
